"use client";

import { useEffect, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { authenticatedRawFetch } from "@/lib/api/client";
import { ApiError } from "@/lib/api/error";
import {
  getGetProgressQueryKey,
  getGetJobAtsLinkQueryKey,
  getGetCandidateDetailQueryKey,
  getListAtsNeedsMergeQueryKey,
} from "@/lib/api/generated";
import { bustPipelineCache } from "../lib/bust-pipeline-cache";

const MAX_ATTEMPTS = 10;
// Per-candidate sync-state hints arrive one-per-candidate; bulk sync would fan
// out into a refetch storm if we invalidated on each. Trailing debounce coalesces
// quiet gaps; max-wait forces a flush during sustained bursts so badges still
// update mid-sync (~1 pipeline refetch / 3s under steady completions).
const CANDIDATE_SYNC_DEBOUNCE_MS = 2000;
const CANDIDATE_SYNC_MAX_WAIT_MS = 3000;
// If the stream goes silent for this long — no events AND no `: keep-alive`
// heartbeat (the server emits one every ~15s when idle) — we treat the
// connection as dead (half-open TCP, proxy cut, laptop sleep) and force a
// reconnect. Without this a silently-dead stream would leave `connected` stuck
// true and the gated pollers disabled → the UI silently stops updating.
// Must comfortably exceed the server heartbeat interval (~2x) so normal idle
// gaps + network jitter never trip a false reconnect.
const HEARTBEAT_TIMEOUT_MS = 30_000;

export interface ParsedEvent {
  event: string;
  data: string;
}

/**
 * Buffer-aware SSE parser (shared shape with useArbitratorStream): splits on
 * `\n\n`, keeps the trailing partial chunk, concatenates multiple `data:` lines.
 * Also used by `useProjectSearchRunsStream`.
 */
export function parseSSE(buffer: string): {
  events: ParsedEvent[];
  rest: string;
} {
  const events: ParsedEvent[] = [];
  let rest = buffer;
  while (true) {
    const idx = rest.indexOf("\n\n");
    if (idx === -1) break;
    const block = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    let evt = "message";
    const dataLines: string[] = [];
    for (const raw of block.split("\n")) {
      const line = raw.replace(/\r$/, "");
      if (!line) continue;
      if (line.startsWith("event:")) {
        evt = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (dataLines.length > 0) {
      events.push({ event: evt, data: dataLines.join("\n") });
    }
  }
  return { events, rest };
}

/**
 * True for the errors `authenticatedRawFetch` surfaces when the session is
 * gone for good (401 after refresh, missing tokens). Reconnecting would only
 * spin until the auth redirect unmounts the caller, so streams stop instead.
 */
export function isTerminalAuthError(err: unknown): boolean {
  if (err instanceof ApiError && err.status === 401) return true;
  if (!(err instanceof Error)) return false;
  return (
    err.message === "No access token" ||
    err.message === "No refresh token" ||
    err.message === "Invalid refresh response"
  );
}

/**
 * On (re)connect, re-sync the source-of-truth REST queries. Events are
 * fire-and-forget hints over Redis pub/sub with no journal, so anything emitted
 * while we were disconnected is lost — an unconditional refetch here self-heals.
 */
function refetchPipeline(qc: QueryClient, jobId: string): void {
  bustPipelineCache(qc, jobId);
  void qc.invalidateQueries({ queryKey: getGetProgressQueryKey(jobId) });
  void qc.invalidateQueries({ queryKey: getGetJobAtsLinkQueryKey(jobId) });
}

/** Mark the pipeline table stale and refetch it (used for coarse change hints). */
function bustPipeline(qc: QueryClient, jobId: string): void {
  bustPipelineCache(qc, jobId);
}

/** Minimal shape of a cached `["pipeline", jobId, ...]` page. */
interface CachedPipelinePage {
  candidates?: { job_candidate_id?: string | null }[] | null;
}

/**
 * Invalidate only the cached pipeline pages that actually contain one of the
 * changed candidates. A per-candidate sync-state change just flips a row's
 * badge — if the row isn't in a loaded page there's nothing on screen to
 * update, so refetching would be pure waste. The visible page refetches
 * (default `refetchType: "active"`); matching inactive pages are marked stale
 * and refresh when revisited. Returns whether anything matched.
 */
function invalidateLoadedPipelinePages(
  qc: QueryClient,
  jobId: string,
  changedIds: Set<string>,
): void {
  const pages = qc.getQueriesData<CachedPipelinePage>({
    queryKey: ["pipeline", jobId],
  });
  for (const [key, data] of pages) {
    const rows = data?.candidates;
    if (!rows) continue;
    if (rows.some((c) => c.job_candidate_id && changedIds.has(c.job_candidate_id))) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }
}

/**
 * Subscribes to `GET /api/jobs/{jobId}/events` (SSE) and turns coarse pipeline
 * change hints into TanStack cache invalidations. Reconnects with capped
 * exponential backoff; a heartbeat watchdog forces an immediate reconnect if the
 * stream goes silent. Returns `{ connected }` so the layout can gate polling
 * fallbacks — when the stream dies, `connected` flips false and pollers resume.
 */
export function useJobPipelineEvents(jobId: string): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (!jobId) {
      setConnected(false);
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    let activeController: AbortController | null = null;

    // Debounced per-candidate sync-state coalescing. Persists across reconnects
    // within this effect so a burst spanning a blip still flushes once.
    let syncDebounce: ReturnType<typeof setTimeout> | null = null;
    let syncMaxWait: ReturnType<typeof setTimeout> | null = null;
    const pendingSyncCandidateIds = new Set<string>();
    const flushCandidateSync = () => {
      if (syncDebounce) clearTimeout(syncDebounce);
      if (syncMaxWait) clearTimeout(syncMaxWait);
      syncDebounce = null;
      syncMaxWait = null;
      const ids = new Set(pendingSyncCandidateIds);
      pendingSyncCandidateIds.clear();
      if (ids.size === 0) return;
      // Only refetch a pipeline page if one of the changed candidates is
      // actually on it — a sync-state hint for an off-screen candidate has
      // nothing to repaint, so we don't hammer the pipeline endpoint for it.
      invalidateLoadedPipelinePages(qc, jobId, ids);
      // Per-candidate surfaces (open profile drawer, sync-modal poll) self-gate
      // on an active observer — invalidating with no mounted query is a no-op,
      // so these are safe to fire for every id without spurious fetches.
      for (const id of ids) {
        void qc.invalidateQueries({ queryKey: ["sync-progress", jobId, id] });
        void qc.invalidateQueries({
          queryKey: getGetCandidateDetailQueryKey(jobId, id),
        });
      }
    };
    const scheduleCandidateSyncFlush = (candidateId: string) => {
      if (candidateId) pendingSyncCandidateIds.add(candidateId);
      if (syncDebounce) clearTimeout(syncDebounce);
      syncDebounce = setTimeout(flushCandidateSync, CANDIDATE_SYNC_DEBOUNCE_MS);
      // Arm max-wait once per pending batch; later events must not reset it.
      if (!syncMaxWait) {
        syncMaxWait = setTimeout(flushCandidateSync, CANDIDATE_SYNC_MAX_WAIT_MS);
      }
    };

    async function run() {
      while (!cancelled) {
        const controller = new AbortController();
        activeController = controller;
        let watchdog: ReturnType<typeof setTimeout> | null = null;
        let watchdogTripped = false;
        const bumpWatchdog = () => {
          if (watchdog) clearTimeout(watchdog);
          watchdog = setTimeout(() => {
            watchdogTripped = true;
            controller.abort();
          }, HEARTBEAT_TIMEOUT_MS);
        };

        try {
          const res = await authenticatedRawFetch(`/api/jobs/${jobId}/events`, {
            method: "GET",
            signal: controller.signal,
            cache: "no-store",
          });
          if (!res.ok || !res.body) {
            if (res.status === 401) {
              setConnected(false);
              return;
            }
            throw new Error(`stream failed: ${res.status}`);
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          bumpWatchdog();

          while (!cancelled) {
            const { done, value } = await reader.read();
            if (done) break;
            // Any byte — real event OR `: keep-alive` — proves the stream is
            // alive, so reset the watchdog.
            bumpWatchdog();
            buffer += decoder.decode(value, { stream: true });
            const parsed = parseSSE(buffer);
            buffer = parsed.rest;
            for (const e of parsed.events) {
              let data: Record<string, unknown> = {};
              try {
                data = JSON.parse(e.data) as Record<string, unknown>;
              } catch {
                // ignore unparseable payloads
              }
              switch (e.event) {
                case "stream_ready":
                  attempt = 0;
                  setConnected(true);
                  refetchPipeline(qc, jobId);
                  break;
                case "import_progress": {
                  // Patch the progress cache directly (no HTTP, no invalidation
                  // storm during bursty imports). useImportProgressSync's
                  // signature effect sees the write and fans out the pipeline /
                  // imports-list invalidations. Recompute `percent` to match the
                  // server's shape since the event carries only raw counts.
                  const total = Number(data.total ?? 0);
                  const successful = Number(data.successful ?? 0);
                  const failed = Number(data.failed ?? 0);
                  const processing = Number(data.processing ?? 0);
                  const done = successful + failed;
                  const percent = total ? Math.round((done * 100) / total) : 0;
                  qc.setQueryData(getGetProgressQueryKey(jobId), {
                    total,
                    processing,
                    successful,
                    failed,
                    percent,
                  });
                  break;
                }
                case "ats_initial_import_completed":
                  void qc.invalidateQueries({
                    queryKey: getGetJobAtsLinkQueryKey(jobId),
                  });
                  bustPipeline(qc, jobId);
                  break;
                case "sync_run_completed":
                  bustPipeline(qc, jobId);
                  // An inbound sync is the only path that DETECTS new needs-merge
                  // links, so refresh the (job-scoped) queue when a run finishes.
                  void qc.invalidateQueries({
                    queryKey: getListAtsNeedsMergeQueryKey(jobId),
                  });
                  // The sync-runs poll (JobSyncChip's "Synced" summary) stands
                  // down while connected, so refresh it here on completion.
                  void qc.invalidateQueries({ queryKey: ["ats-sync-runs"] });
                  void qc.invalidateQueries({
                    queryKey: ["ats-sync-runs-latest"],
                  });
                  break;
                case "candidate_sync_state_changed":
                  scheduleCandidateSyncFlush(String(data.job_candidate_id ?? ""));
                  break;
                case "needs_merge_changed":
                  void qc.invalidateQueries({
                    queryKey: getListAtsNeedsMergeQueryKey(jobId),
                  });
                  break;
                default:
                  // heartbeats / unknown future events: keep-alive only
                  break;
              }
            }
          }

          // Server closed the stream cleanly — fall through to reconnect.
          setConnected(false);
        } catch (err) {
          if ((err as Error).name === "AbortError") {
            // Either unmount teardown (cancelled) or a watchdog-forced reconnect.
            if (cancelled) return;
            setConnected(false);
          } else if (isTerminalAuthError(err)) {
            return;
          } else {
            setConnected(false);
          }
        } finally {
          if (watchdog) clearTimeout(watchdog);
        }

        if (cancelled) return;
        attempt++;
        if (attempt > MAX_ATTEMPTS) return;
        // Watchdog-forced reconnects retry immediately; network errors back off.
        const delay = watchdogTripped
          ? 0
          : Math.min(15_000, 1_000 * 2 ** (attempt - 1));
        await new Promise<void>((resolve) => {
          backoffTimer = setTimeout(resolve, delay);
        });
      }
    }

    void run();

    return () => {
      cancelled = true;
      activeController?.abort();
      if (backoffTimer) clearTimeout(backoffTimer);
      if (syncDebounce) clearTimeout(syncDebounce);
      if (syncMaxWait) clearTimeout(syncMaxWait);
      setConnected(false);
    };
  }, [jobId, qc]);

  return { connected };
}
